import { FileText, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { get } from "@/lib/api/_client";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./flow-instance-widget.i18n";

interface FlowInstanceData {
  id: string;
  name: string | null;
  flowName: string;
  clientName: string;
  status: string;
  url: string;
}

interface FlowInstanceWidgetProps {
  instance: {
    id: string;
    name: string | null;
    flowName: string;
    clientName: string;
    status: string;
    url: string;
  };
  onFlowInstanceClick?: (url: string) => void;
}

export function FlowInstanceWidget({
  instance: initial,
  onFlowInstanceClick,
}: FlowInstanceWidgetProps) {
  const t = useT(T);
  const [data, setData] = useState<FlowInstanceData>(initial);
  const [loading, setLoading] = useState(false);

  // If flowName is empty, fetch from API
  useEffect(() => {
    if (initial.flowName) {
      setData(initial);
      return;
    }
    setLoading(true);
    get<any>(`/internal/flow-instances/${initial.id}/metadata`)
      .then((meta) => {
        // Need flow name too — fetch from the flow
        return get<any>(`/internal/flow-instances/client/${meta.clientId}`).then(
          (instances: any[]) => {
            const match = instances?.find((i: any) => i.id === initial.id);
            if (match) {
              setData({
                id: initial.id,
                name: match.name,
                flowName: match.flow?.name || t.flow,
                clientName: "",
                status: match.status || meta.status,
                url: `/clients/${meta.clientId}/flows/${initial.id}`,
              });
            }
          },
        );
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [initial]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs p-2 rounded bg-muted/50">
        <Loader2 className="h-3 w-3 animate-spin" />
        <span className="text-muted-foreground">{t.loading}</span>
      </div>
    );
  }

  const displayName = data.flowName || data.name || t.flowInstance;

  return (
    <button
      type="button"
      className="w-full text-left flex items-center gap-2 text-xs p-2 rounded bg-muted/50 hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer border border-transparent hover:border-primary/20"
      onClick={() => onFlowInstanceClick?.(data.url || `/flow-instances/${data.id}`)}
    >
      <div className="h-6 w-6 rounded bg-primary/10 flex items-center justify-center flex-shrink-0">
        <FileText className="h-3 w-3 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">
          {displayName}
          {data.name && data.flowName && (
            <span className="text-muted-foreground ml-1">({data.name})</span>
          )}
        </div>
        <div className="text-muted-foreground truncate">
          {data.status && <span className="capitalize">{data.status.replaceAll("_", " ")}</span>}
        </div>
      </div>
    </button>
  );
}
